import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench, Lightning, ShieldCheck, ArrowRight, Engine, Gauge, Trophy } from "@phosphor-icons/react";
import { fetchFeaturedBikes, resolveAssetUrl } from "@/lib/api";
import { useSiteContent } from "@/contexts/SiteContentContext";
import BikeCard from "@/components/BikeCard";
import Marquee from "@/components/Marquee";
import FacebookFeed from "@/components/FacebookFeed";

const SERVICE_ICONS = [Lightning, Wrench, ShieldCheck];
const WORKSHOP_ICONS = [Engine, Gauge, Trophy];

function HeroMedia({ videoUrl, posterUrl }) {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const isVideo = videoUrl && (videoUrl.endsWith(".mp4") || videoUrl.endsWith(".webm") || videoUrl.endsWith(".mov") || videoUrl.includes("/uploads/"));

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    v.loop = true;
    v.setAttribute("webkit-playsinline", "true");
    v.src = resolveAssetUrl(videoUrl);
    const tryPlay = () => v.play().then(() => setVideoReady(true)).catch(() => {});
    if (v.readyState >= 2) tryPlay();
    v.addEventListener("canplay", tryPlay);
    return () => {
      v.removeEventListener("canplay", tryPlay);
      try { v.pause(); } catch {}
    };
  }, [videoUrl, isVideo]);

  if (!isVideo) {
    return <img src={resolveAssetUrl(videoUrl) || resolveAssetUrl(posterUrl)} alt="Hero" className="absolute inset-0 w-full h-full object-cover" />;
  }

  return (
    <>
      <img src={resolveAssetUrl(posterUrl)} alt="Hero" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoReady ? "opacity-0" : "opacity-100"}`} />
      <video
        ref={videoRef}
        data-testid="hero-video"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={resolveAssetUrl(posterUrl)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoReady ? "opacity-100" : "opacity-0"}`}
      />
    </>
  );
}

export default function Home() {
  const content = useSiteContent();
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedBikes()
      .then(setBikes)
      .catch(() => setBikes([]))
      .finally(() => setLoading(false));
  }, []);

  if (!content) return <div className="pt-32 text-white/50 text-center">Φόρτωση...</div>;

  const { hero, services, workshop, cta_strip } = content;

  return (
    <div data-testid="home-page" className="pt-32 md:pt-44">
      <section className="relative min-h-[88vh] overflow-hidden">
        <HeroMedia videoUrl={hero.video_url} posterUrl={hero.poster_url} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/55 to-black/20 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-40 pb-20 md:pb-28">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-overline mb-6">
            {hero.established}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }} className="font-display font-black uppercase tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] max-w-4xl">
            {hero.title_line1}<br/>
            <span className="text-[#E50000]">{hero.title_accent}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="mt-6 max-w-xl text-white/70 text-base md:text-lg">
            {hero.description}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/catalog" data-testid="hero-browse-btn" className="btn-primary text-xs">
              {hero.cta_primary} <ArrowRight className="ml-2" size={16} weight="bold" />
            </Link>
            <Link to="/service" data-testid="hero-book-service-btn" className="btn-outline text-xs">
              {hero.cta_secondary}
            </Link>
          </motion.div>

          <div className="absolute bottom-10 left-6 md:left-12 right-6 md:right-12 flex flex-wrap gap-x-12 gap-y-4 text-xs font-mono text-white/50 uppercase tracking-widest">
            {(hero.stats || []).map((s, i) => (
              <span key={i}><span className="text-[#E50000]">●</span> {s.label}</span>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      <section data-testid="services-section" className="section-pad">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-overline mb-4">{services.overline}</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
              {services.title_line1}<br/> {services.title_line2} <span className="text-[#E50000]">{services.title_accent}</span> {services.title_line3}
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-px bg-white/10">
            {(services.items || []).map((c, i) => {
              const Icon = SERVICE_ICONS[i] || Lightning;
              return (
                <div key={i} className="bg-[#0A0A0A] p-8 md:p-10 group hover:bg-[#141414] transition-colors">
                  <Icon size={32} weight="duotone" className="text-[#E50000]" />
                  <h3 className="mt-6 font-display font-bold text-2xl uppercase tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-white/60 leading-relaxed text-sm">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="featured-catalog-section" className="section-pad bg-[#0A0A0A] border-t border-white/10">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-overline mb-4">Στο showroom</p>
              <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight max-w-xl">
                Επιλεγμένες<br/> μηχανές.
              </h2>
            </div>
            <Link to="/catalog" data-testid="view-all-bikes-link" className="text-sm font-bold uppercase tracking-[0.18em] text-white/70 hover:text-[#E50000] inline-flex items-center gap-2 group">
              Δες όλο τον κατάλογο <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[0,1,2].map(i => <div key={i} className="aspect-[4/3] bg-[#141414] animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {bikes.slice(0, 4).map((b, i) => <BikeCard key={b.id} bike={b} large={i === 0} index={i} />)}
            </div>
          )}
        </div>
      </section>

      <section data-testid="workshop-section" className="section-pad">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative">
            <img src={resolveAssetUrl(workshop.image_url)} alt="Εργαστήριο" className="w-full h-[480px] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-[#E50000] text-white p-6 hidden md:block">
              <p className="font-mono text-xs uppercase tracking-widest opacity-80">{workshop.turnaround_label}</p>
              <p className="font-display font-black text-4xl">{workshop.turnaround_value}</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-overline mb-4">{workshop.overline}</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">
              {workshop.title_line1} <span className="text-[#E50000]">{workshop.title_accent}</span>.
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">{workshop.description}</p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {(workshop.tags || []).map((label, i) => {
                const Icon = WORKSHOP_ICONS[i] || Engine;
                return (
                  <div key={i} className="border border-white/10 p-4">
                    <Icon size={22} weight="duotone" className="text-[#E50000]" />
                    <p className="mt-3 text-xs font-bold uppercase tracking-wider text-white/80">{label}</p>
                  </div>
                );
              })}
            </div>
            <Link to="/service" data-testid="workshop-book-btn" className="btn-primary text-xs mt-10">
              Κλείσε Ραντεβού Σέρβις
            </Link>
          </div>
        </div>
      </section>

      {/* <FacebookFeed /> hidden until public Business Page is available */}

      <section className="border-t border-white/10 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <h3 className="font-display font-black uppercase tracking-tight text-3xl md:text-5xl leading-[0.95]">
            {cta_strip.title_line1}<br/> {cta_strip.title_line2}
          </h3>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/test-ride" data-testid="cta-strip-test-ride" className="btn-primary text-xs">{cta_strip.primary}</Link>
            <Link to="/catalog" data-testid="cta-strip-catalog" className="btn-outline text-xs">{cta_strip.secondary}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
